import {
    OptionStyle,
    OptionWrapperStyle,
    Wrapper
} from "./select.style";
import React, {
    CSSProperties,
    HTMLAttributes,
    MouseEvent, ReactElement,
    ReactNode, useCallback,
    useContext,
    useEffect,
    useId, useLayoutEffect, useMemo,
    useRef,
    useState
} from "react";
import {ButtonWrapper} from "../button/button.style";
import {CustomHelper} from "../../helpers";
import {AnimatePresence, motion} from "motion/react";
import {IconButtonComponent} from "../button/iconButton.component";
import {useMediaQuery} from "styled-breakpoints/use-media-query";
import {theme} from "../../theme";
import {DrawerComponent} from "../drawer/drawer.component";
import {OptionComponentProps, SelectComponentContext, SelectComponentProps} from "./select.type";
import {DrawerProps} from "../drawer/drawer.style";


export const SelectComponent = React.forwardRef(({
                                                     variant,
                                                     size,
                                                     offset = 10,
                                                     label,
                                                     children,
                                                     error,
                                                     success,
                                                     message,
                                                     arrow,
                                                     selected,
                                                     disabled = false,
                                                     onChange,
                                                     multiple,
                                                     onSelectionChange,
                                                     onOpenChange,
                                                     fitToScreenOnMobile = true,
                                                     placeholder,
                                                     defaultValue,
                                                     wrapperProps,
                                                     drawerProps,
                                                     ...triggerProps
                                                 }:
                                                 SelectComponentProps,
                                                 ref
) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    React.useImperativeHandle(ref, () => triggerRef.current!, []);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuUlRef = useRef<HTMLUListElement>(null);

    // Initialize activeId with defaultValue or selected prop
    const [activeId, setActiveId] = useState<string | string[] | null>(() => {
        if (defaultValue !== undefined) {
            return defaultValue;
        }
        return multiple ? (selected as string[] || []) : (selected as string || null);
    });

    const [selectedDisplayValues, setSelectedDisplayValues] = useState<(string)[]>([]);
    const [open, setOpen] = useState(false)
    const [style, setStyle] = useState<CSSProperties>({
        visibility: "hidden",
        position: fitToScreenOnMobile ? "fixed" : "absolute"
    })
    const componentId = useId()
    const isMobile = useMediaQuery((theme as any).breakpoints.down("md"))

    // Sync display values with activeId and children
// Sync display values with activeId and children
    const syncDisplayValues = useCallback(() => {
        if (!children) {
            setSelectedDisplayValues([]);
            return;
        }

        const options = React.Children.toArray(children) as ReactElement<OptionComponentProps>[];

        if (multiple) {
            const activeIds = Array.isArray(activeId) ? activeId : [];
            const displayValues: string[] = [];

            options.forEach(option => {
                if (option.props.value && activeIds.includes(option.props.value)) {
                    // Get the text content from the DOM element instead of React element
                    const displayValue = getTextContentFromReactNode(option.props.children);
                    displayValues.push(displayValue || option.props.value);
                }
            });

            setSelectedDisplayValues(displayValues);
        } else {
            const active = activeId as string | null;
            const selectedOption = options.find(opt => opt.props.value === active);
            const displayValue = selectedOption
                ? getTextContentFromReactNode(selectedOption.props.children) || selectedOption.props.value || ""
                : "";

            setSelectedDisplayValues(displayValue ? [displayValue] : []);
        }
    }, [activeId, children, multiple]);

// Simple helper to extract text from React nodes
    const getTextContentFromReactNode = (node: ReactNode): string => {
        if (typeof node === 'string') return node;
        if (typeof node === 'number') return node.toString();
        if (!node) return '';

        // For React elements, try to get text content from children
        if (React.isValidElement(node)) {
            // @ts-ignore - access children prop
            return getTextContentFromReactNode(node.props.children);
        }

        // For arrays, recursively get text from each item
        if (Array.isArray(node)) {
            return node.map(getTextContentFromReactNode).join('');
        }

        return '';
    };

    const handleSelect = useCallback(
        (key: string, displayValue: string) => {
            if (multiple) {
                // ensure arrays
                const prev = Array.isArray(activeId) ? activeId : [];
                const prevDisplay = [...selectedDisplayValues];

                const alreadySelected = prev.includes(key);

                const newValue = alreadySelected
                    ? prev.filter(v => v !== key)
                    : [...prev, key];

                const newDisplay = alreadySelected
                    ? prevDisplay.filter(v => v !== displayValue)
                    : [...prevDisplay, displayValue];

                setActiveId(newValue);
                setSelectedDisplayValues(newDisplay);
                onSelectionChange?.(newValue, newDisplay);
                return;
            }

            // SINGLE SELECT
            setActiveId(key);
            setSelectedDisplayValues([displayValue]);
            onSelectionChange?.(key, [displayValue]);
            setOpen(false);
        },
        [multiple, activeId, selectedDisplayValues, onSelectionChange]
    );

    const handleOpen = useCallback(() => {
        if (disabled) return;
        setOpen(true);
        onOpenChange?.(true);
    }, [onOpenChange, disabled]);

    const handleClose = useCallback(() => {
        if (disabled) return;
        setOpen(false);
        onOpenChange?.(false);
    }, [onOpenChange, disabled]);

    const handlePositioning = useCallback(() => {
        const tr = triggerRef.current
        const el = menuRef.current
        if (!el || !tr) return;
        const rect = tr.getBoundingClientRect()
        const cRect = el.getBoundingClientRect()
        if (fitToScreenOnMobile && isMobile) {
            setStyle({
                position: "static",
                overflow: "scroll",
                transform: "none",
                zIndex: 9999,
                width: "auto",
                maxWidth: "inherit",
                borderRadius: 0,
                height: "auto",
                bottom: "14px",
                background: "transparent"
            });
        } else if (cRect.height >= (window.innerHeight - rect.bottom)) {
            setStyle({
                position: 'fixed',
                top: rect.top - cRect.height - offset,
                left: rect.left,
                overflow: "auto",
                opacity: 1,
                transformOrigin: "center top 0px",
                transform: "scale(1)",
                zIndex: 99,
                width: rect.width,
            })
        } else {
            setStyle({
                position: 'fixed',
                top: rect.bottom + offset,
                left: rect.left,
                overflow: "auto",
                opacity: 1,
                transformOrigin: "center top 0px",
                transform: "scale(1)",
                zIndex: 99,
                width: rect.width
            })
        }
    }, [offset, fitToScreenOnMobile, isMobile])

    // Sync display values when activeId or children change
    useEffect(() => {
        syncDisplayValues();
    }, [syncDisplayValues]);

    // Handle selected prop changes
    useEffect(() => {
        if (selected === undefined || selected === null) {
            setActiveId(multiple ? [] : null);
        } else {
            setActiveId(selected);
        }
    }, [selected, multiple]);

    // Handle defaultValue on initial render
    useEffect(() => {
        if (defaultValue !== undefined) {
            setActiveId(defaultValue);
        }
    }, [defaultValue]);

    useEffect(() => {
        const onkey = (e: KeyboardEvent) => {
            if (e.key === "Escape" || e.key === "Tab") handleClose()
        }
        const onClickOutside = (e: MouseEvent) => {
            if (menuRef.current &&
                e.target instanceof Node &&
                !menuRef.current.contains(e.target) &&
                !triggerRef.current?.contains(e.target)) {
                handleClose();
            }
        }
        window.addEventListener("keydown", onkey)
        // @ts-ignore
        window.addEventListener("mousedown", onClickOutside)
        return () => {
            window.removeEventListener("keydown", onkey);
            // @ts-ignore
            window.removeEventListener("mousedown", onClickOutside);
        };
    }, [open, handleClose])

    useEffect(() => {
        if (!open) return;
        const handleScroll = (e: Event) => {
            handlePositioning()
        };
        const handleResize = () => handlePositioning();

        window.addEventListener("resize", handleResize);
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            window.removeEventListener("resize", handleResize)
            window.removeEventListener("scroll", handleScroll, true)
        }
    }, [open, handlePositioning])

    useLayoutEffect(() => {
        if (!open) return;
        handlePositioning();
    }, [open, handlePositioning, fitToScreenOnMobile])

    const getDisplayText = () => {
        if (multiple) {
            if (selectedDisplayValues.length > 0) {
                return selectedDisplayValues.join(", ");
            }
            return placeholder || "Select...";
        }
        return selectedDisplayValues[0] || (placeholder || "Select...");
    };

    drawerProps = {
        $side: "bottom",
        showCloseButton: drawerProps?.showCloseButton || true,
        closeOnOutside: !!drawerProps?.closeOnOutside,
        onOpenChange: () => {
            setOpen((prevState) => !prevState)
            onOpenChange?.(open)
        },
        header: label,
        open,
        $backdrop: true,
        $size: "lg",
        overlay: true,
        ...drawerProps
    }
    const triggerPropsWithHandlers: React.ButtonHTMLAttributes<HTMLButtonElement> = {
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if ("ontouchstart" in window) e.preventDefault();
            handleOpen();
        },
        "aria-haspopup": "listbox",
        "aria-expanded": open,
        disabled,
        ...triggerProps
    };
    return (
        <SelectComponentContext.Provider value={{
            componentId,
            handleSelect,
            multiple: !!multiple,
            activeId
        }}>
            <Wrapper {...wrapperProps}>
                <ButtonWrapper type={"button"} {...triggerPropsWithHandlers} ref={triggerRef} $fullWidth
                               className={`select-btn text-capitalize ${triggerProps?.className || ""}`}
                               $iconPosition={"end"}>
                    {getDisplayText()}
                    {CustomHelper.renderIcon(<svg style={{fontSize: "15px"}} viewBox="0 0 24 24" fill="none"
                                                  xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M11.9995 16.8C11.2995 16.8 10.5995 16.53 10.0695 16L3.54953 9.48001C3.25953 9.19001 3.25953 8.71001 3.54953 8.42001C3.83953 8.13001 4.31953 8.13001 4.60953 8.42001L11.1295 14.94C11.6095 15.42 12.3895 15.42 12.8695 14.94L19.3895 8.42001C19.6795 8.13001 20.1595 8.13001 20.4495 8.42001C20.7395 8.71001 20.7395 9.19001 20.4495 9.48001L13.9295 16C13.3995 16.53 12.6995 16.8 11.9995 16.8Z"
                            fill="currentColor"/>
                    </svg>)}
                </ButtonWrapper>
                <AnimatePresence mode={"wait"}>
                    {open &&
                        (
                            isMobile ?
                                <DrawerComponent
                                    {...drawerProps as DrawerProps}
                                >
                                    <OptionWrapperStyle style={style} ref={menuRef}>
                                        <motion.div
                                            key={`${componentId}-rb-select-content`}
                                            initial={{opacity: 0, y: -8}}
                                            animate={{opacity: 1, y: 0}}
                                            exit={{opacity: 0, y: -8}}
                                            transition={{duration: 0.18, ease: [0.22, 1, 0.36, 1]}}
                                            style={{willChange: "transform, opacity"}}
                                        >
                                            <ul ref={menuUlRef} tabIndex={0}>
                                                {children}
                                            </ul>
                                        </motion.div>
                                    </OptionWrapperStyle>
                                </DrawerComponent>
                                :
                                <OptionWrapperStyle style={style} ref={menuRef} className={"shadow-md"}>
                                    {label && <motion.div
                                        key={`${componentId}-rb-header-content`}
                                        initial={{opacity: 0, y: -8}}
                                        animate={{opacity: 1, y: 0}}
                                        exit={{opacity: 0, y: -8}}
                                        transition={{duration: 0.18, ease: [0.22, 1, 0.36, 1]}}
                                        style={{willChange: "transform, opacity"}}
                                        className="label">
                                        <b className={"grotesk-fnt"}>{label}</b>
                                        <IconButtonComponent
                                            size={"sm"}
                                            onClick={handleClose}
                                            className={"close-btn"}
                                            variant={"outline"}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M1.87571 0.696715C1.55028 0.371278 1.02264 0.371277 0.697203 0.696715C0.371766 1.02215 0.371766 1.54979 0.697203 1.87523L4.82199 6.00002L0.697203 10.1248C0.371766 10.4502 0.371766 10.9779 0.697203 11.3033C1.02264 11.6288 1.55028 11.6288 1.87571 11.3033L6.0005 7.17853L10.1253 11.3033C10.4507 11.6288 10.9784 11.6288 11.3038 11.3033C11.6292 10.9779 11.6292 10.4502 11.3038 10.1248L7.17902 6.00002L11.3038 1.87523C11.6292 1.54979 11.6292 1.02215 11.3038 0.696715C10.9784 0.371278 10.4507 0.371278 10.1253 0.696715L6.0005 4.8215L1.87571 0.696715Z"
                                                    fill="black"/>
                                            </svg>
                                        </IconButtonComponent>
                                    </motion.div>}

                                    <motion.div
                                        key={`${componentId}-rb-select-content`}
                                        initial={{opacity: 0, y: -8}}
                                        animate={{opacity: 1, y: 0}}
                                        exit={{opacity: 0, y: -8}}
                                        transition={{duration: 0.18, ease: [0.22, 1, 0.36, 1]}}
                                        style={{willChange: "transform, opacity"}}
                                    >
                                        <ul ref={menuUlRef} tabIndex={0}>
                                            {children}
                                        </ul>
                                    </motion.div>
                                </OptionWrapperStyle>
                        )
                    }
                </AnimatePresence>
            </Wrapper>
        </SelectComponentContext.Provider>
    )
})

export const OptionComponent = ({children, value, icon, ...props}: OptionComponentProps) => {
    const ctx = useContext(SelectComponentContext)
    if (!ctx) {
        throw new Error("Cant have option component without its select component provider")
    }
    const {handleSelect, multiple, activeId: selectedValues} = ctx
    const optionRef = useRef<HTMLLIElement>(null);
    const isSelected = () => {
        if (!optionRef.current) return false;
        const finalValue = value !== undefined ? value : optionRef.current.id;
        return multiple
            ? Array.isArray(selectedValues) && selectedValues.includes(finalValue)
            : selectedValues === finalValue;
    };
    return (
        <OptionStyle
            tabIndex={-1}
            role={"option"}
            ref={optionRef}
            onClick={(e) => {
                handleSelect(value || "", e.currentTarget.textContent || "");
            }}
            data-selected={isSelected()}
        >
            {icon && <span className="rb-option-icon">{icon}</span>}
            <span className="rb-option-label">{children}</span>
            <div className="rb-option-extra">
                <div className="radio-input"></div>
            </div>
        </OptionStyle>
    )
}